import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect, useMemo, useState
} from 'react'
import { EMPTY, catchError, finalize, from, takeUntil, tap } from 'rxjs'
import { ProfileApi } from 'src/api'
import { Button, Input, useAnalytic } from 'src/components'
import { ETrackingEvent } from 'src/enums'
import { useBehaviorMapper, useUnsubscribe, useValidation } from 'src/hooks'
import { IconPencilStroke } from 'src/icons'
import { ICompanyUserModel } from 'src/interfaces'
import { SnackbarService } from 'src/services'
import { AuthModule } from 'src/store'
import { getFirstCompany } from 'src/utils'
import { registerPasswordSchema } from 'src/validation'
import { object } from 'yup'
import { ProfileBlock } from './profile-block'

const schema = object().shape({
  recruiterCompanies: registerPasswordSchema.fields.recruiterCompanies
})

interface IProps {
  screen?: string
}

export const AssociatedCompaniesSection: FC<IProps> = ({ screen }) => {
  const { eventHandler } = useAnalytic(screen)
  const unsubscribe$ = useUnsubscribe()
  const profile = useBehaviorMapper(AuthModule.profile$)
  const company = useMemo(() => getFirstCompany(profile), [profile])
  const recruiterCompanies = useMemo(
    () => company?.CompanyUser?.recruiterCompanies,
    [company?.CompanyUser?.recruiterCompanies]
  )

  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<{
    isRecruiter: boolean
    recruiterCompanies?: ICompanyUserModel['recruiterCompanies']
  }>({
    isRecruiter: true,
    recruiterCompanies: []
  })
  const { errors, validate, reset } = useValidation({
    data: formData,
    schema
  })

  useEffect(() => {
    if (!editing) {
      reset()
      setFormData({ isRecruiter: true })
    } else {
      const length = Math.max(recruiterCompanies?.length || 0, 2)
      const associatedCompanies = Array.from({ length }, (_, index) => recruiterCompanies?.[index] || { name: '', website: '' })
      setFormData({
        isRecruiter: true,
        recruiterCompanies: associatedCompanies
      })
    }
  }, [editing, recruiterCompanies, reset])

  const updateRecruiterCompanies = useCallback((e: ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      recruiterCompanies: prev.recruiterCompanies?.map((company, i) => i === index ? { ...company, [name]: value } : company)
    }))
  }, [])

  const onSubmit = useCallback(async () => {
    const { isValid, value: values } = await validate()
    if (!isValid || !values) {
      return
    }
    setLoading(true)
    values.recruiterCompanies = values.recruiterCompanies?.filter((company) => Boolean(company?.name?.trim()))
    from(ProfileApi.update(values))
      .pipe(
        // switchMap(() => ProfileApi.getProfile()),
        // tap(({ data }) => AuthModule.updateProfile(data)),
        tap(() => {
          if (company?.CompanyUser) {
            company.CompanyUser.recruiterCompanies = values.recruiterCompanies
            AuthModule.updateProfile({
              ...profile,
              companies: [company]
            })
          }
        }),
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => setLoading(false))
      )
      .subscribe(() => {
        setEditing(false)
        SnackbarService.success('Roles updated successfully')
      })
  }, [company, profile, unsubscribe$, validate])

  return (
    <ProfileBlock>
      <div className="fx fx-ai-center gap-2 semibold-18 txt-black-01">
        Associated Companies
        <Button
          variant="icon"
          className="ml-auto"
          disabled={loading || editing}
          onClick={() => setEditing(true)}
        >
          <IconPencilStroke size={20} color="currentColor"/>
        </Button>
      </div>

      <div className="fx-column gap-3 txt-black-01">
        {editing
          ? formData.recruiterCompanies?.map((company, index) => (
            <div className="fx-1 fx gap-2" key={index}>
              <Input
                id={`recruiterCompanies-${index}-name`}
                wrapperClassName="fx-1"
                name="name"
                placeholder="Enter company name"
                value={company.name || ''}
                onChange={(e) => updateRecruiterCompanies(e, index)}
                error={errors.getError(`recruiterCompanies[${index}].name`)}
              />
              <Input
                id={`recruiterCompanies-${index}-website`}
                wrapperClassName="fx-1"
                name="website"
                placeholder="https://www.example.com"
                value={company.website || ''}
                onChange={(e) => updateRecruiterCompanies(e, index)}
                error={errors.getError(`recruiterCompanies[${index}].website`)}
              />
            </div>
          ))
          : recruiterCompanies?.map((company, index) => (
            <div key={index} className="fx-1 fx fx-ai-center gap-7 body-14" style={{ height: '40px' }}>
              <div className="heading-14 txt-grey-04 w-30 online-text">{company.name}</div>
              {company.website}
            </div>
          ))}
      </div>

      {editing && (
        <div className="fx fx-ai-center fx-jc-flex-end gap-3">
          <Button
            variant="secondary"
            disabled={loading}
            onClick={eventHandler(ETrackingEvent.INPUT_SETTING_CANCEL_EDIT_ROLES, () => setEditing((prev) => !prev))}
          >
            Cancel
          </Button>
          <Button
            disabled={loading}
            onClick={eventHandler(ETrackingEvent.INPUT_SETTING_SAVE_EDIT_ROLES, onSubmit)}
          >
            Save
          </Button>
        </div>
      )}
    </ProfileBlock>
  )
}
